import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  AtSign,
  BrainCircuit,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleCheckBig,
  Eye,
  EyeOff,
  Inbox,
  Languages,
  LockKeyhole,
  Mail,
  Radar,
  SearchCheck,
  Send,
  ShieldCheck,
  Sparkles,
  Star,
  UserRound,
  UsersRound,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../services/api";
import { Button } from "../components/ui/Button";
import { Field, TextInput } from "../components/ui/Form";
import { BLUESUN_OFFICIAL_ICON, BLUESUN_WEBSITE } from "../lib/brand";
import { APP_VERSION } from "../lib/releaseHistory";

const INITIAL_LOGIN = { identifier: "", password: "", remember: true };
const INITIAL_REGISTER = { username: "", display_name: "", email: "", password: "", confirm_password: "" };

const WORKFLOW_STEPS = [
  { icon: Inbox, index: "01", title: "邮件自动入库", copy: "持续同步业务邮箱，保留原文、附件和完整沟通上下文。" },
  { icon: BrainCircuit, index: "02", title: "AI 理解与背调", copy: "提取真实需求，结合公开信息判断身份、价值与合作风险。" },
  { icon: Languages, index: "03", title: "专业英文回复", copy: "参考产品知识与外贸函电规范，生成可编辑的英文回复草稿。" },
  { icon: Send, index: "04", title: "人工审核推进", copy: "由业务员确认收件人、正文和签名，再完成发送与后续跟进。" },
];

const CAPABILITIES = [
  { icon: Radar, eyebrow: "Customer Intelligence", title: "不只读邮件，更理解客户。", copy: "从国家、公司和联系人三个维度组织背调结论，把公开来源、风险信号与商业价值放在同一视图。", tags: ["身份核验", "公开来源", "商业价值", "风险提示"], tone: "blue" },
  { icon: Sparkles, eyebrow: "Reply Copilot", title: "每封回复，都像资深业务员写的。", copy: "根据客户等级控制篇幅与问题数量，结合 BLUESUN 产品知识，给出明确的下一步行动。", tags: ["英文函电", "产品话术", "分级回复", "人工可编辑"], tone: "amber" },
  { icon: UsersRound, eyebrow: "Human in the Loop", title: "AI 提速，决定权始终在人。", copy: "草稿、背调和评分都可复核。系统记录处理状态，但不会绕过业务员直接作出关键承诺。", tags: ["人工审核", "发送预览", "可追溯", "权限控制"], tone: "green" },
];

export default function AuthPage({ onAuthenticated }) {
  const [mode, setMode] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [login, setLogin] = useState(INITIAL_LOGIN);
  const [register, setRegister] = useState(INITIAL_REGISTER);
  const [showBackTop, setShowBackTop] = useState(false);

  useEffect(() => {
    const nodes = document.querySelectorAll("[data-reveal]");
    if (!nodes.length) return undefined;
    if (!("IntersectionObserver" in window)) {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14, rootMargin: "0px 0px -8%" });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const updateBackTop = () => setShowBackTop(window.scrollY > 420);
    updateBackTop();
    window.addEventListener("scroll", updateBackTop, { passive: true });
    return () => window.removeEventListener("scroll", updateBackTop);
  }, []);

  const switchMode = (nextMode, shouldScroll = false) => {
    setMode(nextMode);
    setMessage("");
    setShowPassword(false);
    if (shouldScroll) window.setTimeout(() => document.getElementById("workspace-access")?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      if (mode === "login") {
        const result = await api.login(login);
        onAuthenticated(result.user);
      } else {
        if (register.password !== register.confirm_password) throw new Error("两次输入的密码不一致。");
        const result = await api.register(register);
        if (result.authenticated) onAuthenticated(result.user);
        else {
          switchMode("login");
          setLogin((value) => ({ ...value, identifier: register.username }));
          setMessage(result.message || "注册成功，请登录。");
        }
      }
    } catch (error) {
      setMessage(error.message || "请求失败，请检查输入后重试。");
    } finally {
      setBusy(false);
    }
  };

  const setRegisterValue = (key) => (event) => setRegister((value) => ({ ...value, [key]: event.target.value }));

  return (
    <main className="marketing-page">
      <a className="marketing-skip" href="#workspace-access">跳到登录区域</a>

      <header className="marketing-nav" aria-label="首页导航">
        <a className="marketing-brand" href="#top" aria-label="BLUESUN 外贸 AI 询盘运营平台首页">
          <span><img src={BLUESUN_OFFICIAL_ICON} alt="BLUESUN" loading="eager" decoding="async" /></span>
          <strong>BLUESUN<small>企业专属 · AI 询盘运营平台</small></strong>
        </a>
        <nav aria-label="产品介绍">
          <a href="#workflow">工作方式</a><a href="#capabilities">核心能力</a><a href="#workspace">产品界面</a><a href="#security">数据安全</a>
        </nav>
        <div className="marketing-nav__actions">
          <button type="button" onClick={() => switchMode("login", true)}>登录</button>
          <button type="button" className="is-primary" onClick={() => switchMode("register", true)}>开始使用 <ArrowRight size={15} /></button>
        </div>
      </header>

      <section className="marketing-hero" id="top">
        <div className="energy-orbit energy-orbit--one" aria-hidden="true" />
        <div className="energy-orbit energy-orbit--two" aria-hidden="true" />
        <div className="energy-grid" aria-hidden="true" />
        <div className="marketing-hero__content">
          <div className="marketing-hero__copy">
            <span className="marketing-eyebrow"><i aria-hidden="true" /> Built for BLUESUN</span>
            <h1>用 AI 看懂客户，<br /><em>用专业赢得信任。</em></h1>
            <p>这是为 BLUESUN 业务团队打造的外贸 AI 询盘运营平台。它把收件、客户理解、专业回复和人工审核，组织成一条清晰的成交路径。</p>
            <div className="marketing-signal" aria-label="系统工作流程">
              <span><Mail size={15} /><small>客户来信</small></span>
              <i aria-hidden="true" />
              <span><SearchCheck size={15} /><small>识别需求</small></span>
              <i aria-hidden="true" />
              <span><BrainCircuit size={15} /><small>AI 辅助</small></span>
              <i aria-hidden="true" />
              <span><Send size={15} /><small>人工推进</small></span>
            </div>
            <div className="marketing-hero__actions">
              <button type="button" className="marketing-primary" onClick={() => switchMode("login", true)}>进入工作台 <ArrowRight size={17} /></button>
              <a href="#workflow">了解产品 <ArrowDown size={16} /></a>
            </div>
            <ul className="marketing-proof" aria-label="BLUESUN 企业信息">
              <li><strong>2013</strong><span>品牌创立</span></li><li><strong>172+</strong><span>国家与地区足迹</span></li><li><strong>12GW+</strong><span>累计组件出货</span></li>
            </ul>
          </div>

          <section className="landing-auth-card" id="workspace-access" aria-label="登录或注册外贸 AI 询盘运营平台">
            <div className="landing-auth-card__glow" aria-hidden="true" />
            <div className="auth-assurance"><ShieldCheck size={15} />企业数据已加密保护</div>
            <form onSubmit={submit}>
              <header className="auth-form-header">
                <span>INQUIRY OPERATIONS WORKSPACE</span>
                <h2>{mode === "login" ? "欢迎回来" : "创建业务账号"}</h2>
                <p>{mode === "login" ? "继续处理今天值得优先跟进的询盘。" : "注册后即可配置邮箱与 AI 服务。"}</p>
              </header>
              <div className="auth-mode" role="tablist" aria-label="账号入口">
                <button type="button" role="tab" aria-selected={mode === "login"} className={mode === "login" ? "is-active" : ""} onClick={() => switchMode("login")}>登录</button>
                <button type="button" role="tab" aria-selected={mode === "register"} className={mode === "register" ? "is-active" : ""} onClick={() => switchMode("register")}>注册</button>
              </div>
              {mode === "login" ? (
                <div className="auth-fields">
                  <Field label="用户名或邮箱" required><AuthInput icon={AtSign}><TextInput autoComplete="username" value={login.identifier} onChange={(event) => setLogin({ ...login, identifier: event.target.value })} placeholder="请输入用户名或工作邮箱" required /></AuthInput></Field>
                  <Field label="密码" required><AuthInput icon={LockKeyhole} action={<PasswordToggle shown={showPassword} onClick={() => setShowPassword(!showPassword)} />}><TextInput type={showPassword ? "text" : "password"} autoComplete="current-password" value={login.password} onChange={(event) => setLogin({ ...login, password: event.target.value })} placeholder="请输入登录密码" required /></AuthInput></Field>
                  <label className="remember"><input type="checkbox" checked={login.remember} onChange={(event) => setLogin({ ...login, remember: event.target.checked })} /><span>保持登录状态</span><small>仅建议在个人设备上启用</small></label>
                </div>
              ) : (
                <div className="auth-register-grid">
                  <Field label="用户名" required><AuthInput icon={UserRound}><TextInput autoComplete="username" value={register.username} onChange={setRegisterValue("username")} placeholder="用于登录系统" required /></AuthInput></Field>
                  <Field label="显示名称" required><AuthInput icon={UserRound}><TextInput autoComplete="name" value={register.display_name} onChange={setRegisterValue("display_name")} placeholder="业务员姓名" required /></AuthInput></Field>
                  <Field label="工作邮箱" required className="span-2"><AuthInput icon={AtSign}><TextInput type="email" autoComplete="email" value={register.email} onChange={setRegisterValue("email")} placeholder="name@company.com" required /></AuthInput></Field>
                  <Field label="密码" required><AuthInput icon={LockKeyhole}><TextInput type={showPassword ? "text" : "password"} autoComplete="new-password" minLength={8} value={register.password} onChange={setRegisterValue("password")} placeholder="至少 8 位" required /></AuthInput></Field>
                  <Field label="确认密码" required><AuthInput icon={LockKeyhole} action={<PasswordToggle shown={showPassword} onClick={() => setShowPassword(!showPassword)} />}><TextInput type={showPassword ? "text" : "password"} autoComplete="new-password" minLength={8} value={register.confirm_password} onChange={setRegisterValue("confirm_password")} placeholder="再次输入密码" required /></AuthInput></Field>
                  <p className="auth-password-hint span-2"><LockKeyhole size={12} />密码至少 8 位，建议同时包含字母与数字。</p>
                </div>
              )}
              {message ? <div className="auth-alert" role="alert" aria-live="assertive">{message}</div> : null}
              <Button type="submit" variant="primary" loading={busy} className="auth-submit" icon={ArrowRight}>{mode === "login" ? "进入工作台" : "创建账号"}</Button>
              <p className="auth-submit-note"><LockKeyhole size={13} />不会在浏览器中明文保存邮箱授权码与 API Key</p>
            </form>
          </section>
        </div>
        <a className="marketing-scroll-cue" href="#workflow" aria-label="继续向下了解产品"><span>SCROLL TO EXPLORE</span><ArrowDown size={15} /></a>
      </section>

      <section className="marketing-transition" aria-label="产品价值"><p>真正的 AI，不是替你发邮件。</p><strong>而是把每一次判断所需的信息，提前准备好。</strong></section>

      <section className="marketing-section marketing-workflow" id="workflow">
        <div className="marketing-section__heading" data-reveal><span>01 · ONE CONTINUOUS WORKFLOW</span><h2>一封邮件进入，<br />一条商机流程开始。</h2><p>无需在邮箱、搜索引擎、翻译工具和文档之间反复切换。系统把上下文连续传递到每一步。</p></div>
        <div className="workflow-stage" data-reveal>
          <div className="workflow-stage__rail" aria-hidden="true"><i /><i /><i /></div>
          {WORKFLOW_STEPS.map(({ icon: Icon, index, title, copy }, position) => <article key={title} className={position === 1 ? "is-featured" : ""}><span className="workflow-stage__index">{index}</span><span className="workflow-stage__icon"><Icon size={21} /></span><h3>{title}</h3><p>{copy}</p><CheckCircle2 size={17} className="workflow-stage__check" aria-hidden="true" /></article>)}
        </div>
      </section>

      <section className="marketing-section marketing-capabilities" id="capabilities">
        <div className="marketing-section__heading is-light" data-reveal><span>02 · BUILT FOR GLOBAL SALES</span><h2>比“生成一封邮件”，<br />多做关键的三步判断。</h2><p>AI 先理解客户、核验信息、判断价值，再帮助业务员组织一封可以真正推进合作的回复。</p></div>
        <div className="capability-grid">
          {CAPABILITIES.map(({ icon: Icon, eyebrow, title, copy, tags, tone }) => <article className={`capability-card is-${tone}`} key={title} data-reveal><span className="capability-card__icon"><Icon size={22} /></span><small>{eyebrow}</small><h3>{title}</h3><p>{copy}</p><ul>{tags.map((tag) => <li key={tag}><Check size={13} />{tag}</li>)}</ul></article>)}
        </div>
      </section>

      <section className="marketing-section marketing-workspace" id="workspace">
        <div className="workspace-copy" data-reveal><span>03 · FOCUS ON THE NEXT ACTION</span><h2>不是数据看板，<br />是业务员的行动工作台。</h2><p>列表告诉你谁值得优先处理，详情告诉你为什么，回复区给出下一步。每个区域都围绕成交推进，而不是堆叠信息。</p><ul><li><CircleCheckBig size={18} /><span><strong>价值优先</strong><small>星级、状态、国家与产品一眼可见</small></span></li><li><CircleCheckBig size={18} /><span><strong>证据可查</strong><small>AI 结论与公开来源保持关联</small></span></li><li><CircleCheckBig size={18} /><span><strong>发送可控</strong><small>收件人、抄送、签名和原文统一预览</small></span></li></ul></div>
        <ProductPreview />
      </section>

      <section className="marketing-section marketing-security" id="security">
        <div className="security-visual" data-reveal aria-hidden="true"><div className="security-core"><ShieldCheck size={36} /><span>HUMAN<br />CONTROL</span></div><i className="security-node security-node--one"><Mail size={18} /></i><i className="security-node security-node--two"><LockKeyhole size={18} /></i><i className="security-node security-node--three"><UsersRound size={18} /></i></div>
        <div className="security-copy" data-reveal><span>04 · SECURITY BY WORKFLOW</span><h2>把效率交给 AI，<br />把边界留给企业。</h2><p>敏感凭证加密存储，邮件发送由业务员审核确认，AI 结论保留依据，重要操作可追溯。</p><div className="security-points"><span><ShieldCheck size={18} /><strong>凭证加密</strong><small>授权码与 API Key 不在浏览器明文保存</small></span><span><UserRound size={18} /><strong>人工审核</strong><small>关键回复在发送前由业务员确认</small></span><span><SearchCheck size={18} /><strong>来源可查</strong><small>背调结论关联公开检索信息</small></span><span><Zap size={18} /><strong>流程可恢复</strong><small>失败任务可识别、记录和重新处理</small></span></div></div>
      </section>

      <section className="marketing-final" data-reveal><div className="marketing-final__grid" aria-hidden="true" /><span><Sparkles size={15} /> AI INQUIRY OPERATIONS · FOR BLUESUN</span><h2>让下一封询盘，<br />从一开始就被认真对待。</h2><p>连接业务邮箱，建立属于团队的客户理解、专业回复与跟进流程。</p><div><button type="button" className="marketing-primary" onClick={() => switchMode("register", true)}>创建业务账号 <ArrowRight size={17} /></button><button type="button" onClick={() => switchMode("login", true)}>已有账号，直接登录</button></div></section>

      <footer className="marketing-footer"><a className="marketing-brand" href="#top"><span><img src={BLUESUN_OFFICIAL_ICON} alt="" loading="lazy" decoding="async" /></span><strong>BLUESUN<small>企业专属 · AI 询盘运营平台</small></strong></a><p>BLUESUN 数智化产品团队 · {APP_VERSION}</p><div><a href={BLUESUN_WEBSITE} target="_blank" rel="noreferrer">BLUESUN 官网 <ChevronRight size={14} /></a><a href="#top">返回顶部</a></div></footer>
      <button
        type="button"
        className={`marketing-backtop${showBackTop ? " is-visible" : ""}`}
        aria-label="返回顶部登录区域"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <ArrowUp size={17} />
        <span>返回登录</span>
      </button>
    </main>
  );
}

function ProductPreview() {
  return (
    <div className="product-preview" data-reveal aria-label="BLUESUN 询盘工作台界面示意">
      <div className="product-preview__topbar"><span><i /><i /><i /></span><strong>Global Inquiry Operations</strong><em><ShieldCheck size={12} /> 已保护</em></div>
      <div className="product-preview__body">
        <aside><header><strong>询盘队列</strong><small>28 封待处理</small></header><article className="is-active"><span>AM</span><div><strong>Alim Rahman</strong><small>Bangladesh · BESS</small><p>30/50kW industrial energy storage...</p></div><time>09:42</time></article><article><span>JD</span><div><strong>James Dube</strong><small>South Africa · Solar</small><p>Off-grid system for commercial site...</p></div><time>09:31</time></article><article><span>MK</span><div><strong>Mert Kaya</strong><small>Türkiye · Distribution</small><p>Long-term cooperation for PV...</p></div><time>09:08</time></article></aside>
        <section><header><div><strong>Alim Rahman</strong><small>30/50kW Industrial Energy Storage System</small></div><span>待审核</span></header><nav><b>邮件与客户</b><span>AI 分析</span><span>回复与发送</span></nav><div className="product-preview__customer"><span><small>客户</small><strong>Alim Rahman</strong></span><span><small>国家 / 地区</small><strong>Bangladesh</strong></span><span><small>商业价值</small><strong className="preview-stars"><Star size={13} fill="currentColor" /><Star size={13} fill="currentColor" /><Star size={13} fill="currentColor" /><Star size={13} fill="currentColor" /><Star size={13} /></strong></span></div><div className="product-preview__mail"><small>客户邮件</small><p>We are looking for a 50kW hybrid energy storage solution for a commercial project. Please share the suitable configuration...</p></div><div className="product-preview__ai"><span><Sparkles size={15} /> AI 建议</span><p>需求明确，建议优先确认负载功率、备电时长和项目交付地点，再提供初步系统配置。</p><button type="button" tabIndex={-1}>审核回复 <ArrowRight size={13} /></button></div></section>
      </div>
    </div>
  );
}

function PasswordToggle({ shown, onClick }) {
  return <button type="button" aria-label={shown ? "隐藏密码" : "显示密码"} onClick={onClick}>{shown ? <EyeOff size={17} /> : <Eye size={17} />}</button>;
}

function AuthInput({ icon: Icon, action, children }) {
  return <div className="auth-input"><Icon size={17} aria-hidden="true" />{children}{action}</div>;
}
